import React, {useEffect, useRef, useState} from 'react';
import {Button} from 'antd';
import Header from '../../components/molecules/Header/component';
import Footer from '../../components/molecules/Footer/component';
import Seo from '../../components/atoms/Seo/component';
import {openSignup} from '../../utils/helpers';
import productService from '../../services/productService';
import {trackEvent} from '../../analytics/posthog';
import {useTrackOnView} from '../../analytics/useTrackOnView';
import './style.css';

const openPromoterSignup = () => {
    trackEvent('promoter_guide_start_as_promoter_clicked', {
        selected_role: 'promoter',
        signup_source: 'promoter_guide_start_as_promoter'
    });
    openSignup('promoter', 'promoter_guide_start_as_promoter');
};

type ProductPrice = {
    unit_amount?: number;
    currency?: string;
    recurring?: {
        interval?: string;
        interval_count?: number;
    };
};

type Product = {
    name: string;
    default_price?: ProductPrice;
    images?: string[];
    description?: string;
};

const benefits = [
    {
        image: '/assets/images/promoter-guide/earning-per-click.png',
        title: 'Clear click rates',
        text: 'Know the $/click before you share, so every campaign starts with clear earning potential.'
    },
    {
        image: '/assets/images/promoter-guide/campaign-choice.png',
        title: 'Promote what fits',
        text: 'Pick campaigns that match your niche, audience, content style and channels.'
    },
    {
        image: '/assets/images/promoter-guide/built-in-tools.png',
        title: 'Real-time visibility',
        text: 'Track clicks, earnings and campaign performance from your dashboard.'
    }
];

const steps = [
    {
        title: 'Sign up as Promoter',
        text: 'Create your account and choose the Promoter role.'
    },
    {
        title: 'Browse campaigns',
        text: 'Choose campaigns that match your niche, audience and content style.'
    },
    {
        title: 'Copy, share and track',
        text: 'Copy your affiliate link from My campaigns, then follow performance in Dashboard and earnings in Wallet.'
    }
];

const earningItems = [
    'Campaign selected and click rate',
    'Clicks generated',
    'Earnings per campaign',
    'Wallet balance and withdrawals'
];

const workflowGroups = [
    {
        title: 'Visible in your account',
        items: ['Dashboard', 'My campaigns', 'Wallet', 'Transactions']
    },
    {
        title: 'Payouts & withdrawals',
        text: 'Earnings are credited to your Promotify Wallet based on tracked activity. Withdrawals are available through Wallet and recorded in Transactions, subject to platform rules and verification checks.',
        items: []
    }
];

const getPlanSortValue = (product: Product) => {
    const price = product.default_price;
    const interval = price?.recurring?.interval;
    const intervalCount = price?.recurring?.interval_count || 1;

    if (interval === 'month') return intervalCount;
    if (interval === 'year') return 12 * intervalCount;

    return Number.MAX_SAFE_INTEGER;
};

const formatPlanFromProduct = (product: Product) => {
    const price = product.default_price;
    const amount = typeof price?.unit_amount === 'number' ? price.unit_amount / 100 : 0;
    const currency = price?.currency?.toUpperCase?.() || 'USD';
    const interval = price?.recurring?.interval;
    const intervalCount = price?.recurring?.interval_count || 1;
    const cadence = interval
        ? intervalCount === 1
            ? `Per ${interval}`
            : `Every ${intervalCount} ${interval}s`
        : '';
    return {
        name: product.name.replace(' - Promoter', ''),
        price: `$${amount.toFixed(2)}`,
        cadence,
        currency,
        image: product.images?.[0],
        description: product.description
    };
};

const faqs = [
    {
        question: 'Do I need a huge following?',
        answer: 'No. If your audience trusts you and you can drive clicks, you can earn.'
    },
    {
        question: 'Can I promote more than one campaign?',
        answer: 'Yes. One subscription covers unlimited campaign promotions.'
    },
    {
        question: 'How do I get paid?',
        answer: 'Your earnings go to your Wallet, and you can withdraw from there.'
    },
    {
        question: 'What should I promote?',
        answer: 'Campaigns that match your niche, such as restaurant content for a food audience or travel offers for a travel audience.'
    }
];

const scrollToPlan = () => {
    trackEvent('promoter_guide_see_pricing_clicked', {
        selected_role: 'promoter'
    });
    document.getElementById('promoter-plan')?.scrollIntoView({behavior: 'smooth'});
};

const PromoterGuidePage: React.FC = () => {
    const [promoterProducts, setPromoterProducts] = useState<Product[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const pricingSectionRef = useRef<HTMLElement>(null);
    const planOptionsRef = useRef<HTMLDivElement>(null);

    useTrackOnView(pricingSectionRef, 'promoter_guide_pricing_section_viewed', {
        selected_role: 'promoter',
        section_name: 'promoter_pricing'
    });
    useTrackOnView(planOptionsRef, 'promoter_guide_plan_viewed', {
        selected_role: 'promoter',
        plan_name: 'Promoter'
    });

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'auto'});
        trackEvent('promoter_guide_page_viewed', {
            selected_role: 'promoter'
        });
    }, []);

    useEffect(() => {
        productService.getProducts('promoter', {skipSubscriptionCheck: true})
            .then(products => {
                setPromoterProducts(Array.isArray(products) ? products : []);
            })
            .finally(() => setIsLoadingProducts(false));
    }, []);

    const promoterPlans = [...promoterProducts]
        .sort((a, b) => getPlanSortValue(a) - getPlanSortValue(b))
        .map(formatPlanFromProduct);

    return (
        <div className="promoterGuide">
            <Seo
                title="Promoter Guide | Promotify"
                description="Learn how promoters use Promotify to browse campaigns, share tracked links, and earn from verified clicks."
                canonicalPath="/promoter-guide"
            />
            <Header/>

            <main>
                <section className="promoterGuideHero">
                    <div className="promoterGuideDecor decorRing"/>
                    <div className="promoterGuideDecor decorPlus">+</div>
                    <div className="promoterGuideDecor decorDot"/>
                    <div className="promoterGuideContainer promoterGuideHeroGrid">
                        <div className="promoterGuideHeroCopy">
                            <span className="promoterGuideEyebrow">Promoter guide</span>
                            <h1>
                                Earn per click promoting campaigns
                                <span> you believe in.</span>
                            </h1>
                            <p>
                                Browse campaigns, grab your unique link and track clicks and earnings in your dashboard.
                            </p>
                            <div className="promoterGuideActions">
                                <Button type="primary" onClick={openPromoterSignup}>Start as Promoter</Button>
                                <Button onClick={scrollToPlan}>See pricing</Button>
                            </div>
                            <small>Sign up for free to explore. Subscribe when you decide to promote campaigns.</small>
                        </div>

                        <div className="promoterGuideHeroImage">
                            <div className="promoterGuideSnapshot">
                                <strong>Quick snapshot</strong>
                                <ul>
                                    <li>Pick campaigns from the marketplace.</li>
                                    <li>Get your unique affiliate link instantly.</li>
                                    <li>Earn per click (CPC) you drive.</li>
                                    <li>Track clicks and earnings in real time.</li>
                                    <li>Withdraw from your wallet when ready.</li>
                                </ul>
                            </div>
                            <img
                                src="/assets/images/promoter-guide/promoter-main.png"
                                alt="Promoter campaign growth illustration"
                            />
                        </div>
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideIntro">
                    <div className="promoterGuideContainer promoterGuideNarrow">
                        <span className="promoterGuideEyebrow">Why PromotifyAI</span>
                        <h2>People click because they trust where the recommendation comes from.</h2>
                        <p>
                            Promotify gives promoters a simple way to find campaigns, share tracked links, and see
                            clicks, earnings and rates before they promote.
                        </p>
                    </div>

                    <div className="promoterGuideContainer promoterGuideCards">
                        {benefits.map((card, index) => (
                            <article className={`promoterGuideCard card${index + 1}`} key={card.title}>
                                <img src={card.image} alt="" aria-hidden="true"/>
                                <h3>{card.title}</h3>
                                <p>{card.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideStepsSection">
                    <div className="promoterGuideContainer promoterGuideNarrow">
                        <span className="promoterGuideEyebrow">How it works</span>
                        <h2>3 simple steps</h2>
                    </div>
                    <div className="promoterGuideContainer promoterGuideSteps">
                        {steps.map((step, index) => (
                            <article className="promoterGuideStep" key={step.title}>
                                <div className="promoterGuideStepTop">
                                    <span className="promoterGuideNumber">{index + 1}</span>
                                    {index < steps.length - 1 && (
                                        <span className="promoterGuideStepArrow" aria-hidden="true"/>
                                    )}
                                </div>
                                <div>
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideDashboardSection">
                    <div className="promoterGuideContainer promoterGuideDashboard">
                        <div>
                            <span className="promoterGuideEyebrow">What you can track</span>
                            <h2>What we track inside your dashboard.</h2>
                            <ul className="promoterGuideCheckList">
                                {earningItems.map(item => <li key={item}>{item}</li>)}
                            </ul>
                            <p className="promoterGuideDashboardNote">
                                Visible in your Dashboard, My campaigns, Wallet and Transactions.
                            </p>
                        </div>
                        <div className="promoterGuideMockup" aria-hidden="true">
                            <div className="promoterGuideMockupStats">
                                <div>
                                    <span>Earnings</span>
                                    <strong>$428.60</strong>
                                </div>
                                <div>
                                    <span>Clicks</span>
                                    <strong>1,284</strong>
                                </div>
                                <div>
                                    <span>Links</span>
                                    <strong>12</strong>
                                </div>
                            </div>
                            <div className="promoterGuideChart">
                                <span style={{height: '38%'}}/>
                                <span style={{height: '46%'}}/>
                                <span style={{height: '52%'}}/>
                                <span style={{height: '64%'}}/>
                                <span style={{height: '70%'}}/>
                                <span style={{height: '86%'}}/>
                            </div>
                            <div className="promoterGuideRows">
                                <div><span>Creator tools</span><span>Active</span></div>
                                <div><span>Wellness offer</span><span>Active</span></div>
                                <div><span>Local event</span><span>Active</span></div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideSection promoterGuideControlSection">
                    <div className="promoterGuideContainer promoterGuideControlGrid">
                        {workflowGroups.map(group => (
                            <article className="promoterGuideControlCard" key={group.title}>
                                <h2>{group.title}</h2>
                                {group.text && <p>{group.text}</p>}
                                {group.items.length > 0 && (
                                    <ul>
                                        {group.items.map(item => <li key={item}>{item}</li>)}
                                    </ul>
                                )}
                            </article>
                        ))}
                    </div>
                </section>

                <section ref={pricingSectionRef} className="promoterGuideSection promoterGuidePlanSection" id="promoter-plan">
                    <div className="promoterGuideContainer promoterGuideNarrow">
                        <span className="promoterGuideEyebrow">Pricing package</span>
                        <h2>Promoter Plan</h2>
                    </div>

                    <div className="promoterGuidePlan">
                        <div className="promoterGuidePlanTop">
                            <div>
                                <h3>How to win as a promoter</h3>
                                <ul className="promoterGuidePlanFeatures">
                                    <li>Browse and pick active campaigns</li>
                                    <li>One subscription, unlimited campaigns</li>
                                    <li>Unique affiliate link generation</li>
                                    <li>Earn per click you drive</li>
                                    <li>Detailed performance reports</li>
                                </ul>
                                <div className="promoterGuidePlanPractices">
                                    <h3>How to promote well</h3>
                                    <ul>
                                        <li>Match campaigns to your audience.</li>
                                        <li>Share a clear reason people should click.</li>
                                        <li>Test formats across posts, stories and links.</li>
                                        <li>Keep content consistent with campaign guidance.</li>
                                    </ul>
                                </div>
                            </div>
                            <div>
                                <div ref={planOptionsRef} className="promoterGuidePlanOptions">
                                    {isLoadingProducts && (
                                        <div className="promoterGuidePlanStatus">Loading promoter plans...</div>
                                    )}
                                    {!isLoadingProducts && promoterPlans.length === 0 && (
                                        <div className="promoterGuidePlanStatus">Promoter plans are unavailable right now.</div>
                                    )}
                                    {!isLoadingProducts && promoterPlans.map(plan => (
                                        <article className="promoterGuidePlanOption" key={plan.name}>
                                            <img
                                                className="promoterGuidePlanImage"
                                                src={plan.image || '/assets/images/promotify-transparent.png'}
                                                alt={plan.name}
                                            />
                                            <div>
                                                <h3>{plan.name}</h3>
                                                {plan.description && (
                                                    <p className="promoterGuidePlanDescription">
                                                        <em>{plan.description}</em>
                                                    </p>
                                                )}
                                            </div>
                                            <p className="promoterGuidePlanPrice">
                                                {plan.price}<span> {plan.currency}</span>
                                            </p>
                                        </article>
                                    ))}
                                </div>
                            </div>
                            <div className="promoterGuidePlanCta">
                                <Button type="primary" onClick={openPromoterSignup}>
                                    Sign up
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideFaqSection">
                    <div className="promoterGuideContainer">
                        <div className="promoterGuideFaqCard">
                            <div>
                                <h2>Frequently asked questions</h2>
                                {faqs.map(faq => (
                                    <details
                                        key={faq.question}
                                        onToggle={(event) => {
                                            if ((event.currentTarget as HTMLDetailsElement).open) {
                                                trackEvent('promoter_guide_faq_opened', {
                                                    selected_role: 'promoter',
                                                    faq_question: faq.question
                                                });
                                            }
                                        }}
                                    >
                                        <summary>{faq.question}</summary>
                                        <p>{faq.answer}</p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="promoterGuideAnimationSection">
                    <video
                        src="/assets/videos/logo-animation-dark.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        aria-label="Promotify animated logo"
                    />
                </section>
            </main>

            <Footer/>
        </div>
    );
};

export default PromoterGuidePage;
